var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useEffect, useState } from 'react';
typeof window !== 'undefined' &&
    !window.__DS_WEB_VERSION__ &&
    (window.__DS_WEB_VERSION__ = '2.4.0');
import { textInputDefaultProps } from './props';
import * as S from './styles';
import { isEmpty, isNotEmpty } from '../../helpers';
import { Spinner } from '../Spinner';
import { SuperiorContent } from './SuperiorContent';
import { InferiorContent } from './InferiorContent';
import { Flex } from '../Flex';
import { getQuantTyped, validation } from '../TextArea/utils';
import { useUniqueId } from '../../hooks';
const INPUT_ACESSIBILITY_ERROR = '[TextInput - Accessibility Error] Se a prop "label" não for informada no TextInput é necessário informar o atributo "aria-label" para garantir melhor acessibilidade no formulário.';
const INPUT_LOADING_ACESSIBILITY_ERROR = '[TextInput - Accessibility Error] Quando o componente está com state="loading" a propriedade stateMessage é obrigatória.';
const INPUT_LABEL_WITHOUT_ID_ERROR = '[TextInput - Error] Para utilização da label com no componente é obrigatório a utilização de um id válido.';
const runValidations = ({ 'aria-label': ariaLabel, id, label, state, stateMessage }) => {
    if (isEmpty(label) && isEmpty(ariaLabel))
        throw new Error(INPUT_ACESSIBILITY_ERROR);
    if (isNotEmpty(label) && isEmpty(id))
        throw new Error(INPUT_LABEL_WITHOUT_ID_ERROR);
    if (state === 'loading' && isEmpty(stateMessage))
        throw new Error(INPUT_LOADING_ACESSIBILITY_ERROR);
};
/**
 * Componente de input de texto do Design System OLX.
 *
 * É um componente de formulário que adiciona um estilo ao input de texto nativo do HTML.
 *
 * **Importante**: O atributo 'aria-label' é obrigatório quando um label não for informado, para que o componente fique acessível a leitores de tela.
 *
 * Veja mais na página de {@link https://design-system-olx.olxbr.io/?path=/docs/components-form-textinput documentação do componente}.
 */
export const TextInput = forwardRef((props, ref) => {
    var _a;
    const [errorId, setErrorId] = useState('');
    useEffect(() => {
        setErrorId(`error-${Math.floor(Math.random() * 101)}`);
    }, []);
    const { 'aria-label': ariaLabel, className, display, iconLeft, iconRight, id, type = textInputDefaultProps.type, margin, marginBottom, marginLeft, marginRight, marginTop, state, width, size = textInputDefaultProps.size, limitTextSize } = props, rest = __rest(props, ['aria-label', "className", "display", "iconLeft", "iconRight", "id", "type", "margin", "marginBottom", "marginLeft", "marginRight", "marginTop", "state", "width", "size", "limitTextSize"]);
    runValidations(props);
    const hasLimitText = validation(limitTextSize);
    const limitTextID = useUniqueId('ds-textinput-limittext');
    return (React.createElement(S.ComponentWrapper, { "data-ds-component": "DS-TextInput", display: display, margin: margin, marginBottom: marginBottom, marginLeft: marginLeft, marginRight: marginRight, marginTop: marginTop, width: width, className: className },
        React.createElement(SuperiorContent, Object.assign({}, props, { id: id })),
        React.createElement(S.InputContainer, { "data-state": props.state, "data-disabled": props.disabled, size: size, "data-hascontent": !!props.value },
            iconLeft && (React.createElement(S.IconWrapper, { position: "left", size: size }, iconLeft)),
            React.createElement(S.InputField, Object.assign({ id: id, type: type, "aria-busy": state === 'loading', "aria-label": ariaLabel, "aria-invalid": state === 'error', "aria-errormessage": state === 'error' ? errorId : '', "aria-disabled": props.disabled === true }, (hasLimitText && { 'aria-describedby': limitTextID }), { variant: size, ref: ref, maxLength: hasLimitText }, (hasLimitText && {
                onInput: (e) => {
                    const target = e.target;
                    target.value = target.value.slice(0, hasLimitText);
                },
            }), rest)),
            (state === 'loading' || iconRight) && (React.createElement(S.IconWrapper, { position: "right", size: size }, state === 'loading' ? React.createElement(Spinner, { "aria-label": (_a = props.stateMessage) !== null && _a !== void 0 ? _a : '', size: "extra-small" }) : iconRight))),
        React.createElement(Flex, { flexDirection: "row", justifyContent: rest.caption || state === 'error' ? 'space-between' : 'flex-end' },
            React.createElement(InferiorContent, Object.assign({ errorId: errorId }, props)),
            hasLimitText && (React.createElement(S.LimitText, { id: limitTextID, "aria-label": `${getQuantTyped(rest.value)} de ${limitTextSize} caracteres usado` },
                getQuantTyped(rest.value),
                "/",
                limitTextSize)))));
});
TextInput.defaultProps = textInputDefaultProps;
TextInput.displayName = 'TextInput';
